import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import LinkNoPrefetch from '../linkNoPrefetch/LinkNoPrefetch';
import styled, { css, useTheme } from 'styled-components';
import { useUser } from '../../hooks/useUser';
import { Kotijoukot as KotijoukotIcon } from '../../icons/Kotijoukot';
import { PersonMenu as PersonIcon } from '../../icons/PersonMenu';
import { mediaQuery, typography } from '../../theme';
import { colors } from '../../theme/color';
import { useOrder } from '../../state/order/hooks/useOrder';
import { storage } from '../../utils/storage';
import { LOGIN_FROM_STORAGE_KEY } from '../login/useRedirectLoggedinUser';
import { MobileMenu } from '../mobileMenu/MobileMenu';
import { CurrencyFormatter } from '../shoppingCart/components/orderItem/Currency';
import { HeaderLoadingIcon, PizzaNappi, SkipToMainContent, UserMenu } from './components';
import FocusTrap from '@mui/material/Unstable_TrapFocus';
import { SitewideBanner } from './components/SitewideBanner';
import { LocalPath, Path } from '../../routes/components/path';
import { TopAlert } from './components/TopAlert';
import { Cart } from '../../icons/Cart';
import { ActiveDiscountBanner } from './components/ActiveDiscountBanner';
import { usePathname } from 'next/navigation';
import { useHasMounted } from '../../hooks/useHasMounted';

export const hiddenHeaderPaths = [
  Path.AppWebViewGiftCardPayment,
  Path.AppWebViewOrderPayment,
  Path.PaymentRedirect,
];
const minimalHeaderPaths = [Path.Order];

export const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const hasMounted = useHasMounted();

  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);

  const order = useOrder();
  const { user, isCCUser, isUserLoading } = useUser();
  const pathname = usePathname();

  const isMobile = useMediaQuery({ query: mediaQuery.large });
  const theme = useTheme();
  const minimal = minimalHeaderPaths.some((path) => pathname.includes(path));

  // The order state is hydrated from localStorage, so it is empty during SSR
  // and the initial client render. Gate cart figures on mount to avoid a
  // hydration mismatch in the cart icon.
  const itemAmount = hasMounted ? order.items.reduce((sum, cur) => sum + cur.quantity, 0) : 0;
  const totalPrice = hasMounted ? order.getGrandTotalOrZeroIfNoProducts() : 0;

  useEffect(() => {
    if (isMobile) return;
    queueMicrotask(() => {
      setMobileMenuOpen(false);
    });
  }, [isMobile]);

  const handleCloseMobileMenu = (): void => {
    setMobileMenuOpen(false);
    if (!menuButtonRef?.current) return;

    menuButtonRef.current.focus();
  };

  const handleCloseUserMenu = (): void => {
    setUserMenuOpen(false);
    if (!userButtonRef?.current) return;

    userButtonRef.current.focus();
  };

  const handleUserMenuButtonClick = (): void => {
    setMobileMenuOpen(false);
    if (userMenuOpen) {
      handleCloseUserMenu();
    } else {
      setUserMenuOpen(true);
    }
  };

  const handleMobileMenuButtonClick = (): void => {
    setUserMenuOpen(false);
    if (mobileMenuOpen) {
      handleCloseMobileMenu();
    } else {
      setMobileMenuOpen(true);
    }
  };

  return (
    <>
      <SkipToMainContent />
      {!mobileMenuOpen && !minimal && (
        <>
          <SitewideBanner />
          <ActiveDiscountBanner />
        </>
      )}
      <HeaderContainer $menuOpen={mobileMenuOpen} data-testid="header" id="main-header">
        <HeaderContent aria-label="Päänavigaatio">
          <StyledLogoNavLink
            href={Path.Home}
            background="transparent"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Etusivu"
          >
            <StyledLogo src={theme.logo.white} alt="Kotipizza" />
          </StyledLogoNavLink>
          <DesktopNavLinks>
            <StyledNavLink href={Path.Menu} aria-label="Ruokalista">
              Menu
            </StyledNavLink>
            <StyledNavLink href="/menu/perfetta">Luo oma pizza</StyledNavLink>
            {!isCCUser && (
              <StyledNavLink href={user?.subscribedToLoyalty ? Path.LoyaltyBenefits : Path.Loyalty}>
                Edut
              </StyledNavLink>
            )}
            {!isCCUser && <StyledNavLink href={Path.GiftCard}>Lahjakortit</StyledNavLink>}
          </DesktopNavLinks>
          <RightContainer>
            {Boolean(user) && <StyledPizzaNappi onClick={() => setMobileMenuOpen(false)} />}
            {isUserLoading ? (
              <HeaderLoadingIcon alt="ladataan" />
            ) : (
              <>
                {user ? (
                  <FocusTrap open={userMenuOpen} disableRestoreFocus disableAutoFocus>
                    <UserButtonContainer>
                      <UserButton
                        aria-label={`${userMenuOpen ? 'Sulje' : 'Avaa'} Kotijoukot-valikko`}
                        onClick={handleUserMenuButtonClick}
                        data-testid="header-account"
                        ref={userButtonRef}
                      >
                        {user.subscribedToLoyalty ? (
                          <KotijoukotIcon width="36" height="36" />
                        ) : (
                          <PersonIcon width="36" height="36" />
                        )}
                        <StyledNavLinkDescription data-clarity-mask="true">
                          <span translate="no">{user.firstName}</span>
                        </StyledNavLinkDescription>
                      </UserButton>
                      {userMenuOpen && <UserMenu onClose={handleCloseUserMenu} />}
                    </UserButtonContainer>
                  </FocusTrap>
                ) : (
                  <StyledNavLink
                    href={Path.Login}
                    data-testid="header-login"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      storage.setItem(LOGIN_FROM_STORAGE_KEY, pathname);
                    }}
                    aria-label="kirjaudu sisään"
                  >
                    <PersonIcon width="36" height="36" />
                    <StyledNavLinkDescription>Kirjaudu</StyledNavLinkDescription>
                  </StyledNavLink>
                )}
              </>
            )}
            <StyledNavLink
              href={Path.Shoppingcart}
              data-testid="header-cart"
              onClick={() => setMobileMenuOpen(false)}
            >
              <CartIconContainer
                role="status"
                aria-live="polite"
                aria-label={`Tilaus ${CurrencyFormatter.format(totalPrice)}${
                  itemAmount > 0 ? `, ${itemAmount} ${itemAmount > 1 ? 'tuotetta' : 'tuote'}` : ''
                }.`}
              >
                {itemAmount > 0 && (
                  <CartItemAmount aria-hidden="true" data-testid="cart-item-quantity">
                    {itemAmount}
                  </CartItemAmount>
                )}
                <Cart width="34" height="34" />
              </CartIconContainer>
              <Suspense>
                <StyledNavLinkDescription aria-hidden="true">
                  Tilaus {CurrencyFormatter.format(totalPrice)}
                </StyledNavLinkDescription>
              </Suspense>
            </StyledNavLink>
            <MobileMenuContainer>
              <FocusTrap open={mobileMenuOpen} disableRestoreFocus disableAutoFocus>
                <div>
                  <MenuButton
                    ref={menuButtonRef}
                    aria-label={`${mobileMenuOpen ? 'Sulje' : 'Avaa'} mobiilivalikko`}
                    onClick={handleMobileMenuButtonClick}
                  >
                    <HamburgerMenuIcon isMenuOpen={mobileMenuOpen}>
                      <div />
                      <div />
                      <div />
                    </HamburgerMenuIcon>
                  </MenuButton>
                  {mobileMenuOpen && <MobileMenu onClose={handleCloseMobileMenu} />}
                </div>
              </FocusTrap>
            </MobileMenuContainer>
          </RightContainer>
        </HeaderContent>
      </HeaderContainer>
      <TopAlert />
    </>
  );
};

const navButtonStyles = css`
  height: 100%;
  border-radius: 10px;
  outline-offset: -10px;
  transition: all 0.1s ease-in;

  &:hover {
    text-decoration: underline 2px;
    text-underline-offset: 3px;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline-offset: -8px;
    outline: 3px solid ${colors.green.darker(20)};
    text-decoration: underline 2px;
    text-underline-offset: 3px;
  }
`;

const UserButtonContainer = styled.div`
  position: relative;
  height: 100%;
  margin-left: 12px;

  @media ${mediaQuery.large} {
    margin-left: 0px;
  }
`;

const UserButton = styled.button`
  border: 0;
  background: none;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.color?.text.heading};
  position: relative;

  ${navButtonStyles}

  @media ${mediaQuery.medium} {
    padding: 14px 6px;
    max-width: 4rem;
  }
`;

const StyledPizzaNappi = styled(PizzaNappi)`
  @media ${mediaQuery.large} {
    padding: 14px 6px;
    max-width: 3rem;
  }

  @media ${mediaQuery.large} {
    position: absolute;
    left: 3rem;
  }
`;

const HeaderContainer = styled.div<{ $menuOpen: boolean }>`
  width: 100%;
  height: 4.5em;
  position: sticky;
  top: 0;
  background: ${({ theme, $menuOpen }) => ($menuOpen ? colors.dough.base : theme.color?.primary)};
  box-shadow: none;
  z-index: 10;
  transition: background 0.1s ease-in;
`;

const HeaderContent = styled.nav`
  max-width: 1200px;
  margin: auto;
  display: flex;
  align-items: center;
  height: 100%;
`;

const StyledNavLink = styled(LinkNoPrefetch)<{ background?: string; href?: LocalPath }>(
  ({ background = 'transparent', theme }) => css`
    ${typography.desktopNavigationStyle}
    height: 100%;
    padding: 16px 18px;
    color: ${theme.color?.text.heading};
    background: ${background};
    text-decoration: none;
    text-transform: none;
    display: flex;
    align-items: center;

    & img {
      height: 100%;
    }

    ${navButtonStyles}
  `
);

const StyledNavLinkDescription = styled.span`
  ${typography.titleStyle}
  font-weight: 500;
  margin-left: 3px;
  margin-right: 3px;

  @media ${mediaQuery.large} {
    display: none;
  }
`;

const DesktopNavLinks = styled.div`
  display: contents;

  @media ${mediaQuery.large} {
    display: none;
  }
`;

const MobileMenuContainer = styled.div`
  display: none;

  @media ${mediaQuery.large} {
    display: flex;
    align-items: center;
    height: 100%;
  }
`;

const StyledLogoNavLink = styled(StyledNavLink)`
  transition: 0.1s all;
  padding: 14px 18px;
  max-width: 6.25rem;
  border-radius: 0;

  & img {
    width: 100%;
    height: auto;
  }

  &:focus-visible {
    outline: none;
    text-decoration: none;
    border: 3px solid ${colors.black};
  }

  @media ${mediaQuery.large} {
    padding: 14px 0;
    max-width: 4rem;
    position: absolute;
    left: calc(50% - 2rem);
  }
`;

const StyledLogo = styled.img`
  transition: 0.1s padding;
`;

const RightContainer = styled.span`
  display: flex;
  margin-left: auto;
  align-items: center;
  height: 100%;

  ${StyledNavLink} {
    text-transform: none;
    padding: 16px 8px;

    @media ${mediaQuery.medium} {
      padding: 16px 4px;
    }

    @media ${mediaQuery.large} {
      padding: 16px 16px 16px 4px;
    }
  }
`;

export const CartIconContainer = styled.div`
  font-size: ${typography.fontSize.LARGE};
  position: relative;
`;

export const CartItemAmount = styled.div`
  ${typography.textTinyStyle}
  font-weight: 700;
  position: absolute;
  top: -0.25em;
  right: -0.25em;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.color?.notification};
  color: ${colors.textDefault};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuButton = styled.button`
  ${typography.textSmallStyle}
  border: 0;
  background: none;
  padding: 1em 0.5em;
  color: ${({ theme }) => theme.color?.text.heading};
  text-decoration: none;
  text-transform: uppercase;
  margin-left: 0;
  display: flex;
  align-items: center;

  ${navButtonStyles}

  & svg {
    width: 1.3em;
    height: 1.3em;
  }

  @media ${mediaQuery.large} {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const HamburgerMenuIcon = styled.div<{ isMenuOpen: boolean }>`
  --bar-size: 2px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 100%;
  margin-left: 8px;

  & > div {
    height: var(--bar-size);
    width: 100%;
    background-color: ${({ theme }) => theme.color?.text.heading};
    display: block;
    border-radius: 5px;
    transition: 0.3s ease;

    &:first-child {
      transform: translateY(calc(-2.5 * var(--bar-size)));
    }

    &:last-child {
      transform: translateY(calc(2.5 * var(--bar-size)));
    }

    ${({ isMenuOpen }) =>
      isMenuOpen &&
      css`
        &:first-child {
          transform: translateY(var(--bar-size)) rotateZ(-45deg);
        }

        &:nth-child(2) {
          opacity: 0;
        }

        &:last-child {
          transform: translateY(calc(-1 * var(--bar-size))) rotateZ(45deg);
        }
      `}
  }
`;
